import type { SectionType } from "../types";
import type { GenerationContext, SurfaceCompleter } from "./generate";
import { generateSurface, recommendDepth } from "./generate";
import { renderSurfaceSpec } from "./render";
import type { StylePack } from "./render";
import { deriveStylePackFromDom } from "./style-pack";
import type { Catalog } from "./validate";
import type { RemoteSurfaceGenerator } from "./client";
import type { SurfaceDepth, SurfaceSpec, SurfaceTrigger } from "./surface-spec";

/**
 * The canvas — GemSales' single surface. Many entry points (AI-question chips,
 * chat icon, search) all open this same near-fullscreen space, per the vision:
 * a generated commerce UI needs room to present, not a 360px chat bubble.
 *
 * This closes the loop end-to-end: chip → openCanvas → deriveStylePackFromDom →
 * generateSurface → validate → renderSurfaceSpec → mount. The model call is still
 * the injected `complete` seam; `templateCompleter` runs it offline (no key).
 */

export interface CanvasStoreContext {
  store: string;
  product?: { ref: string; title: string; price?: number; image?: string };
  source?: SurfaceTrigger["source"];
  intentCluster?: string;
  intentScore?: number;
  cold?: boolean;
  /** Placement-chosen depth; overrides the recommendDepth fallback. */
  depth?: SurfaceDepth;
  /** Cross-session anonymous shopper id + basket total — forwarded for personalization. */
  anonymousId?: string;
  cartSubtotal?: number;
  /** Specific basket line-items (from /cart.js) so the surface completes THIS cart. */
  cartItems?: Array<{ title: string; qty?: number }>;
}

export type SurfaceEmit = (event: string, props?: Record<string, unknown>) => void;

export interface OpenCanvasOptions {
  query: string;
  /** Client-generates + validates locally (offline/template). One of complete|generate. */
  complete?: SurfaceCompleter;
  /** Server-generates over HTTP and returns a validated spec. One of complete|generate. */
  generate?: RemoteSurfaceGenerator;
  context: CanvasStoreContext;
  /** Telemetry sink for surface lifecycle events (opened/rendered/cta_click/closed). */
  emit?: SurfaceEmit;
  entry?: SurfaceTrigger["entry"];
  sectionAnchor?: SectionType;
  stylePack?: StylePack;
  catalog?: Catalog;
  mount?: HTMLElement;
  doc?: Document;
  /** Placeholder for the in-surface follow-up composer. */
  followupPlaceholder?: string;
}

export interface CanvasReady {
  ok: boolean;
  spec?: SurfaceSpec;
  issues: string[];
}

export interface CanvasHandle {
  element: HTMLElement;
  panel: HTMLElement;
  close: () => void;
  ready: Promise<CanvasReady>;
}

export function openCanvas(options: OpenCanvasOptions): CanvasHandle {
  const doc = options.doc ?? document;
  injectStyles(doc);
  const stylePack = options.stylePack ?? deriveStylePackFromDom({ doc });

  const backdrop = el(doc, "div", "gemsales-canvas-backdrop");
  backdrop.setAttribute("role", "dialog");
  backdrop.setAttribute("aria-modal", "true");

  const panel = el(doc, "div", "gemsales-canvas");
  for (const [name, value] of Object.entries(stylePack.tokens)) {
    if (name.startsWith("--")) panel.style.setProperty(name, value);
  }

  const header = el(doc, "div", "gemsales-canvas__header");
  header.append(
    el(doc, "span", "gemsales-canvas__title", options.query),
    button(doc, "gemsales-canvas__close", "×", "Đóng"),
  );
  const closeBtn = header.querySelector<HTMLButtonElement>(".gemsales-canvas__close");

  const body = el(doc, "div", "gemsales-canvas__body");
  let typing = startTyping(doc, prime(body, options.context.product?.image));

  // Follow-up composer — a persistent bar that turns the one-shot answer into a
  // thread: a new question re-runs the engine in place (same sheet, same store).
  const footer = el(doc, "div", "gemsales-canvas__followup");
  const input = doc.createElement("input");
  input.type = "text";
  input.className = "gemsales-followup__input";
  input.setAttribute("placeholder", options.followupPlaceholder ?? "Hỏi tiếp về sản phẩm…");
  input.setAttribute("aria-label", "Hỏi tiếp");
  const sendBtn = button(doc, "gemsales-followup__send", "→", "Gửi câu hỏi");
  footer.append(input, sendBtn);

  panel.append(header, body, footer);
  backdrop.appendChild(panel);
  (options.mount ?? doc.body).appendChild(backdrop);

  const emit = options.emit;
  emit?.("gemsales.surface.opened", { query: options.query, store: options.context.store });

  let currentQuery = options.query;
  // Conversation memory (this open surface): each answered turn appends {query, one-line summary} so a
  // follow-up is generated WITH the thread, not from scratch. Bounded to the last few turns downstream.
  const history: Array<{ query: string; summary: string }> = [];
  const titleEl = header.querySelector<HTMLElement>(".gemsales-canvas__title");

  const onKey = (event: KeyboardEvent): void => {
    if (event.key === "Escape") close();
  };
  const close = (): void => {
    typing.stop();
    backdrop.remove();
    doc.removeEventListener("keydown", onKey);
    emit?.("gemsales.surface.closed", { query: currentQuery });
  };
  doc.addEventListener("keydown", onKey);
  backdrop.addEventListener("click", (event) => {
    if (event.target === backdrop) close();
  });
  closeBtn?.addEventListener("click", close);
  closeBtn?.focus();

  // Swap the body to a spec's rendered surface (replacing skeleton / a provisional).
  const paint = (spec: SurfaceSpec, provisional: boolean): boolean => {
    const rendered = renderSurfaceSpec(spec, stylePack, options.catalog);
    if (!rendered.root) return false;
    if (provisional) rendered.root.setAttribute("data-provisional", "true");
    typing.stop();
    body.replaceChildren(rendered.root);
    panel.setAttribute("data-guardrail", spec.guardrail.status);
    rendered.root
      .querySelector("button.gemsales-cta")
      // surfaceId + cacheKey ride along so an add-to-cart / order downstream can attribute back to the
      // exact surface (and generation variant) that assisted it — the join key for P3·3 attribution.
      ?.addEventListener("click", () =>
        emit?.("gemsales.surface.cta_click", { query: currentQuery, store: options.context.store, surfaceId: spec.surfaceId, cacheKey: spec.meta.cacheKey }),
      );
    return true;
  };
  const emitRendered = (spec: SurfaceSpec, cached: boolean): void =>
    emit?.("gemsales.surface.rendered", {
      query: currentQuery,
      store: options.context.store,
      surfaceId: spec.surfaceId,
      cacheKey: spec.meta.cacheKey,
      blocks: spec.blocks.length,
      guardrail: spec.guardrail.status,
      depth: spec.intent.depth,
      cached,
    });
  const fail = (issues: string[]): CanvasReady => {
    typing.stop();
    body.replaceChildren(el(doc, "div", "gemsales-canvas__error", "Chưa dựng được gợi ý — thử lại nhé."));
    emit?.("gemsales.surface.failed", { query: currentQuery, issues });
    return { ok: false, issues };
  };

  // One turn: generate + paint the surface for a query. Reused by the first open and
  // by every follow-up, so a thread is just runTurn called again with a new question.
  // Append this turn to the thread (a compact "what we showed"), deduping a re-record of the same query.
  const recordTurn = (query: string, spec: SurfaceSpec): void => {
    const summary = summaryOf(spec);
    if (!summary) return;
    if (history[history.length - 1]?.query === query) history[history.length - 1] = { query, summary };
    else history.push({ query, summary });
    if (history.length > 6) history.splice(0, history.length - 6);
  };

  const runTurn = async (query: string): Promise<CanvasReady> => {
    currentQuery = query;
    if (titleEl) titleEl.textContent = query;
    const ctx = buildContext(options, stylePack, query, history.slice());
    const cacheKey = clientCacheKey(options.context.store, query, ctx.depth, history);

    // 1) Client cache (this session) — a surface built before paints instantly and is final.
    const cached = readClientCache(doc, cacheKey);
    if (cached && paint(cached, false)) {
      emitRendered(cached, true);
      return { ok: true, spec: cached, issues: [] };
    }

    // 2) Template-first — a client template paints immediately, so the shopper reads real
    //    content in a frame instead of watching a spinner. Provisional if the LLM is coming.
    let painted = false;
    if (options.complete) {
      try {
        const t = await generateSurface(ctx, options.complete, options.catalog);
        if (t.ok && t.spec) {
          painted = paint(t.spec, Boolean(options.generate));
          if (painted && !options.generate) {
            emitRendered(t.spec, false);
            recordTurn(query, t.spec);
            return { ok: true, spec: t.spec, issues: t.issues };
          }
        }
      } catch {
        /* fall through to the remote path / skeleton */
      }
    }

    // 3) Remote generate — the best (LLM, bound) result; swap it in when ready and cache it.
    if (options.generate) {
      const g = await options.generate(ctx);
      if (g.ok && g.spec) {
        writeClientCache(doc, cacheKey, g.spec);
        paint(g.spec, false);
        emitRendered(g.spec, false);
        recordTurn(query, g.spec);
        return { ok: true, spec: g.spec, issues: g.issues };
      }
      return painted ? { ok: true, issues: g.issues } : fail(g.issues);
    }

    return painted ? { ok: true, issues: [] } : fail(["no completer or generator provided"]);
  };

  // Re-arm the loading state (skeleton + typing narration) for a follow-up turn.
  const submitFollowUp = (): void => {
    const q = input.value.trim();
    if (!q) return;
    input.value = "";
    input.blur();
    emit?.("gemsales.surface.followup", { query: q, store: options.context.store });
    typing.stop();
    typing = startTyping(doc, prime(body, options.context.product?.image));
    void runTurn(q);
  };
  sendBtn.addEventListener("click", submitFollowUp);
  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitFollowUp();
    }
    event.stopPropagation();
  });

  const ready = runTurn(options.query);
  return { element: backdrop, panel, close, ready };
}

/**
 * Arm the loading state and return the node startTyping fills. When the store gave
 * us the product photo, it shows IMMEDIATELY as a hero above the skeleton — real
 * content in the frame within a paint, so the wait for the LLM reads as "building
 * on top of this" instead of a grey box. The typing line lands under the hero.
 */
function prime(body: HTMLElement, productImage?: string): HTMLElement {
  const doc = body.ownerDocument;
  const loading = el(doc, "div", "gemsales-loading");
  const hero = heroFrame(doc, productImage);
  if (hero) loading.appendChild(hero);
  const skel = buildSkeleton(doc);
  loading.appendChild(skel);
  body.replaceChildren(loading);
  return skel;
}

/** A hero media box with the product image, or null when there's no safe URL. */
function heroFrame(doc: Document, url?: string): HTMLElement | null {
  const safe = url && (/^https?:\/\//i.test(url.trim()) || /^data:image\//i.test(url.trim())) ? url.trim() : "";
  if (!safe) return null;
  const box = el(doc, "div", "gemsales-hero__media gemsales-media");
  box.setAttribute("data-has-image", "true");
  const img = doc.createElement("img");
  img.className = "gemsales-media__img";
  img.src = safe;
  img.alt = "";
  img.loading = "eager";
  box.appendChild(img);
  return box;
}

export interface AiQuestionChipOptions {
  /** Shopper-voice label shown on the chip, e.g. "Combo 3 tháng tiết kiệm bao nhiêu?". */
  question: string;
  anchor: HTMLElement;
  placement?: InsertPosition;
  /** Query seeded into the canvas; defaults to the question. */
  seedQuery?: string;
  canvas: Omit<OpenCanvasOptions, "query">;
  doc?: Document;
}

export function mountAiQuestionChip(options: AiQuestionChipOptions): {
  element: HTMLButtonElement;
  destroy: () => void;
} {
  const doc = options.doc ?? document;
  injectStyles(doc);

  const chip = doc.createElement("button");
  chip.type = "button";
  chip.className = "gemsales-chip";
  chip.setAttribute("data-gemsales-chip", "");
  chip.append(el(doc, "span", "gemsales-chip__spark", "✦"), el(doc, "span", "gemsales-chip__label", options.question));

  const emit = options.canvas.emit;
  chip.addEventListener("click", () => {
    emit?.("gemsales.chip.click", { question: options.question });
    openCanvas({ ...options.canvas, query: options.seedQuery ?? options.question, doc });
  });

  options.anchor.insertAdjacentElement(options.placement ?? "afterend", chip);
  emit?.("gemsales.chip.impression", { question: options.question });
  return { element: chip, destroy: () => chip.remove() };
}

/** Offline completer — returns a canned SurfaceSpec payload (no model key needed). */
export function templateCompleter(payload: { blocks: unknown[]; guardrail?: unknown }): SurfaceCompleter {
  const json = JSON.stringify(payload);
  return async () => json;
}

function buildContext(
  options: OpenCanvasOptions,
  stylePack: StylePack,
  query: string = options.query,
  history: Array<{ query: string; summary: string }> = [],
): GenerationContext {
  const entry = options.entry ?? "ai_question";
  const score = options.context.intentScore ?? 0.6;
  const depth = options.context.depth ?? recommendDepth({ entry, intentScore: score, ...(options.context.cold ? { cold: true } : {}) });

  return {
    store: options.context.store,
    entry,
    query,
    ...(history.length ? { history } : {}),
    intent: { cluster: options.context.intentCluster ?? "general", score },
    depth,
    stylePackRef: stylePack.ref,
    ...(options.sectionAnchor ? { sectionAnchor: options.sectionAnchor } : {}),
    ...(options.context.source ? { source: options.context.source } : {}),
    ...(options.context.product ? { product: options.context.product } : {}),
    ...(options.context.anonymousId ? { anonymousId: options.context.anonymousId } : {}),
    ...(typeof options.context.cartSubtotal === "number" ? { cartSubtotal: options.context.cartSubtotal } : {}),
    ...(options.context.cartItems?.length ? { cartItems: options.context.cartItems } : {}),
  };
}

function el(doc: Document, tag: string, className: string, text?: string): HTMLElement {
  const node = doc.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function button(doc: Document, className: string, text: string, ariaLabel: string): HTMLButtonElement {
  const node = doc.createElement("button");
  node.type = "button";
  node.className = className;
  node.textContent = text;
  node.setAttribute("aria-label", ariaLabel);
  return node;
}

/** A surface-shaped shimmer shown while the first spec is being built — reads as
 *  "content is coming", far calmer than a spinner or a bare loading line. */
function buildSkeleton(doc: Document): HTMLElement {
  const wrap = el(doc, "div", "gemsales-skeleton");
  wrap.setAttribute("aria-label", "Đang dựng gợi ý");
  for (const cls of ["gemsales-sk--title", "gemsales-sk--line", "gemsales-sk--line gemsales-sk--short", "gemsales-sk--card", "gemsales-sk--row", "gemsales-sk--cta"]) {
    wrap.appendChild(el(doc, "div", `gemsales-sk ${cls}`));
  }
  return wrap;
}

/**
 * Typewriter narration during the (LLM) wait — types progress lines with a blinking
 * caret so a long generation reads as active work rather than a frozen skeleton.
 * Returns a stop() the paint/fail paths call before swapping in the real content, so
 * it never slows the actual content (which is what the shopper is waiting for).
 */
function startTyping(doc: Document, host: HTMLElement): { stop: () => void } {
  const win = doc.defaultView;
  const line = el(doc, "div", "gemsales-typing");
  const text = el(doc, "span", "gemsales-typing__text");
  line.append(text, el(doc, "span", "gemsales-typing__caret"));
  host.prepend(line);
  if (!win) return { stop: () => undefined };
  const lines = ["Đang đọc sản phẩm…", "Tìm gói tiết kiệm nhất cho bạn…", "Soạn so sánh & bằng chứng…", "Dựng gợi ý riêng cho bạn…"];
  let li = 0;
  let ci = 0;
  let erasing = false;
  let timer = 0;
  const step = (): void => {
    const msg = lines[li % lines.length] ?? "";
    if (!erasing) {
      ci += 1;
      text.textContent = msg.slice(0, ci);
      if (ci >= msg.length) {
        erasing = true;
        timer = win.setTimeout(step, 1200);
      } else {
        timer = win.setTimeout(step, 42 + Math.random() * 46);
      }
    } else {
      ci -= 2;
      text.textContent = msg.slice(0, Math.max(0, ci));
      if (ci <= 0) {
        erasing = false;
        li += 1;
        ci = 0;
        timer = win.setTimeout(step, 280);
      } else {
        timer = win.setTimeout(step, 24);
      }
    }
  };
  timer = win.setTimeout(step, 250);
  return { stop: () => win.clearTimeout(timer) };
}

/** Client-side session cache: re-opening the same chip paints instantly (no round-trip). */
function clientCacheKey(store: string, query: string, depth: number, history: Array<{ query: string }> = []): string {
  const thread = history.length ? `:${history.map((t) => t.query).join(">")}` : "";
  return `gcsurf:${store}:${depth}:${query}${thread}`.slice(0, 240);
}

/** A compact "what we showed" for the thread: the lead headline + the products featured. */
function summaryOf(spec: SurfaceSpec): string {
  const headline = spec.blocks.map((b) => b.slots?.copy?.headline || b.slots?.copy?.title || b.slots?.copy?.body).find(Boolean);
  const refs = [...new Set(spec.blocks.flatMap((b) => b.binding?.products ?? []))].slice(0, 3);
  const parts = [headline?.slice(0, 90), refs.length ? `featured ${refs.join(", ")}` : ""].filter(Boolean);
  return parts.join(" · ").slice(0, 140);
}
function readClientCache(doc: Document, key: string): SurfaceSpec | null {
  try {
    const raw = doc.defaultView?.sessionStorage?.getItem(key);
    return raw ? (JSON.parse(raw) as SurfaceSpec) : null;
  } catch {
    return null; // storage blocked / bad JSON → treat as a miss
  }
}
function writeClientCache(doc: Document, key: string, spec: SurfaceSpec): void {
  try {
    doc.defaultView?.sessionStorage?.setItem(key, JSON.stringify(spec));
  } catch {
    /* quota / blocked → skip; the server cache still holds it */
  }
}

const STYLE_ID = "gemsales-canvas-styles";

export function injectStyles(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CANVAS_CSS;
  (doc.head ?? doc.documentElement).appendChild(style);
}

// SÀN CHẠM 44px CHO MỌI ĐIỀU KHIỂN TRONG SURFACE — §9.1 (Apple HIG / WCAG 2.5.5).
//
// Đo 07/09/2026 trên một storefront thật, tất cả đều DƯỚI sàn:
//     .gemsales-chip            267x32 và 229x32   (padding 7px + 13px/1.25 = đúng 32)
//     .gemsales-composer__x     26x26   <- cách DUY NHẤT để đóng composer trên điện thoại
//     .gemsales-canvas__close   30x30
//     .gemsales-composer__send  38x38
//     .gemsales-followup__send  38x38
//     .gemsales-search-hint     111x36
// Bấm trượt cái ✕ 26px là chạm vào storefront phía sau.
//
// CHÚ THÍCH NẰM Ở ĐÂY, NGOÀI CHUỖI. Bản đầu tôi viết nó BÊN TRONG `CANVAS_CSS`, và `minify: true`
// không bao giờ đụng vào nội dung chuỗi — 468 byte lời giải thích nội bộ đi theo bundle tới MỌI
// storefront. Cũng vì nằm trong template literal mà một dấu backtick trong chú thích đã cắt đứt
// chuỗi và cho ra tám lỗi TS1005.
//
// `min-height` (không phải `height`) để nhãn dài vẫn nở ra được; các nút vuông thì `height` cố định
// được nâng thẳng lên 44 vì chúng là ô vuông thật.
const CANVAS_CSS = `
.gemsales-chip{display:inline-flex;align-items:center;gap:7px;cursor:pointer;border:1px solid var(--gem-color-primary,#111);background:var(--gem-color-surface,#fff);color:var(--gem-color-text,#111);border-radius:var(--gem-radius-pill,999px);padding:7px 13px 7px 11px;min-height:44px;font:550 13px/1.25 var(--gem-font-body,system-ui);box-shadow:0 6px 18px rgba(0,0,0,.12)}
.gemsales-chip__spark{color:var(--gem-color-primary,#111);font-size:12px}
.gemsales-fab{position:fixed;right:20px;bottom:20px;z-index:2147483646;display:inline-flex;align-items:center;gap:9px;height:48px;padding:0 18px 0 13px;border:0;border-radius:999px;cursor:pointer;background:var(--gem-color-primary,#161310);color:var(--gem-color-surface,#fff);font:640 14px/1 var(--gem-font-body,system-ui);box-shadow:0 14px 34px -12px rgba(0,0,0,.5);transition:transform .16s ease,box-shadow .16s ease}
.gemsales-fab:hover{transform:translateY(-2px);box-shadow:0 18px 40px -12px rgba(0,0,0,.55)}
.gemsales-fab__gem{width:24px;height:24px;border-radius:7px;background:conic-gradient(from 210deg,var(--gem-color-primary,#0d6e63),var(--gem-color-accent,#e0533d),var(--gem-color-primary,#0d6e63))}
.gemsales-composer{position:fixed;right:20px;bottom:20px;z-index:2147483647;width:min(340px,calc(100vw - 40px));background:var(--gem-color-surface,#fff);color:var(--gem-color-text,#211c17);border:1px solid var(--gem-color-border,#e7e1d8);border-radius:var(--gem-radius-lg,20px);box-shadow:0 26px 70px -20px rgba(0,0,0,.5);overflow:hidden;font-family:var(--gem-font-body,system-ui);animation:gemsales-sheet-up .34s cubic-bezier(.16,1,.3,1)}
.gemsales-composer__head{display:flex;align-items:center;gap:9px;padding:13px 15px;border-bottom:1px solid var(--gem-color-border,#eee)}
.gemsales-composer__gem{width:22px;height:22px;border-radius:6px;background:conic-gradient(from 210deg,var(--gem-color-primary,#0d6e63),var(--gem-color-accent,#e0533d),var(--gem-color-primary,#0d6e63))}
.gemsales-composer__title{flex:1;font:650 14px/1.2 var(--gem-font-body,system-ui)}
.gemsales-composer__x{border:0;background:var(--gem-color-surface-alt,#f2ede6);color:var(--gem-color-muted,#6b6b6b);width:44px;height:44px;border-radius:8px;font-size:15px;cursor:pointer;line-height:1}
.gemsales-composer__field{display:flex;align-items:center;gap:8px;padding:12px 13px}
.gemsales-composer__input{flex:1;min-width:0;height:38px;border:1px solid var(--gem-color-border,#e0d9cd);border-radius:var(--gem-radius-md,11px);background:var(--gem-color-surface,#fff);color:var(--gem-color-text,#211c17);padding:0 12px;font:500 14px/1 var(--gem-font-body,system-ui);outline:none}
.gemsales-composer__input:focus{border-color:var(--gem-color-primary,#111);box-shadow:0 0 0 3px color-mix(in srgb,var(--gem-color-primary,#111) 14%,transparent)}
.gemsales-composer__send{flex:none;width:44px;height:44px;border:0;border-radius:var(--gem-radius-md,11px);background:var(--gem-color-accent,var(--gem-color-primary,#111));color:#fff;font-size:17px;cursor:pointer;display:grid;place-items:center}
.gemsales-composer__hint{padding:0 15px 13px;font-size:11.5px;color:var(--gem-color-muted,#9a938a)}
.gemsales-slot{display:inline-flex;align-items:center;gap:9px;cursor:pointer;border:1px dashed var(--gem-color-primary,#111);background:color-mix(in srgb,var(--gem-color-primary,#111) 6%,var(--gem-color-surface,#fff));color:var(--gem-color-text,#211c17);border-radius:var(--gem-radius-md,12px);padding:12px 16px;font:600 14px/1.3 var(--gem-font-body,system-ui)}
.gemsales-slot__spark{color:var(--gem-color-primary,#111)}
.gemsales-search-hint{position:absolute;z-index:2147483646;display:inline-flex;align-items:center;gap:8px;background:var(--gem-color-surface,#fff);border:1px solid var(--gem-color-primary,#111);border-radius:10px;padding:8px 11px;font:600 12.5px/1.2 var(--gem-font-body,system-ui);color:var(--gem-color-text,#211c17);box-shadow:0 10px 26px -12px rgba(0,0,0,.4);cursor:pointer;min-height:44px}
.gemsales-search-hint__badge{font:700 10px/1 var(--gem-font-body,system-ui);letter-spacing:.05em;text-transform:uppercase;color:var(--gem-color-primary,#0d6e63);border:1px solid var(--gem-color-primary,#0d6e63);border-radius:5px;padding:3px 5px}
@media (prefers-reduced-motion: reduce){.gemsales-composer{animation:none}.gemsales-fab{transition:none}}
.gemsales-canvas-backdrop{position:fixed;inset:0;z-index:2147483647;background:rgba(15,12,10,.28);display:flex;align-items:flex-end;justify-content:center;padding:0;animation:gemsales-fade .36s ease-out;will-change:opacity}
.gemsales-canvas{position:relative;width:min(940px,100%);max-height:88vh;display:flex;flex-direction:column;overflow:hidden;background:var(--gem-color-bg,var(--gem-color-surface,#fff));color:var(--gem-color-text,#211c17);border:1px solid var(--gem-color-border,#e7e1d8);border-bottom:0;border-radius:var(--gem-radius-lg,22px) var(--gem-radius-lg,22px) 0 0;box-shadow:0 -22px 80px rgba(15,12,10,.34);font-family:var(--gem-font-body,system-ui);animation:gemsales-sheet-up .62s cubic-bezier(.16,1,.3,1);will-change:transform,opacity}
.gemsales-canvas::before{content:"";position:absolute;top:8px;left:50%;transform:translateX(-50%);width:38px;height:4px;border-radius:999px;background:var(--gem-color-border,#ddd4c7);z-index:3}
@keyframes gemsales-fade{from{opacity:0}to{opacity:1}}
@keyframes gemsales-sheet-up{from{transform:translateY(80px);opacity:.5}to{transform:translateY(0);opacity:1}}
.gemsales-canvas__header{position:sticky;top:0;z-index:2;flex:none;display:flex;align-items:center;gap:12px;padding:19px 22px 14px;border-bottom:1px solid var(--gem-color-border,#eee);background:color-mix(in srgb,var(--gem-color-surface,#fff) 88%,transparent);backdrop-filter:blur(8px)}
.gemsales-canvas__title{flex:1;font:600 13px/1.3 var(--gem-font-body,system-ui);color:var(--gem-color-muted,#8a8178);letter-spacing:.01em}
.gemsales-canvas__close{appearance:none;border:0;background:var(--gem-color-surface-alt,#f2ede6);color:var(--gem-color-muted,#6b6b6b);width:44px;height:44px;border-radius:var(--gem-radius-sm,9px);font-size:18px;line-height:1;cursor:pointer;flex:none}
.gemsales-canvas__body{flex:1;overflow:auto;padding:24px 22px 30px}
.gemsales-canvas__followup{flex:none;display:flex;align-items:center;gap:9px;padding:12px 16px;border-top:1px solid var(--gem-color-border,#eee);background:color-mix(in srgb,var(--gem-color-surface-alt,#f2ede6) 60%,var(--gem-color-surface,#fff))}
.gemsales-followup__input{flex:1;min-width:0;height:38px;border:1px solid var(--gem-color-border,#e0d9cd);border-radius:var(--gem-radius-md,11px);background:var(--gem-color-surface,#fff);color:var(--gem-color-text,#211c17);padding:0 13px;font:500 14px/1 var(--gem-font-body,system-ui);outline:none}
.gemsales-followup__input:focus{border-color:var(--gem-color-primary,#111);box-shadow:0 0 0 3px color-mix(in srgb,var(--gem-color-primary,#111) 14%,transparent)}
.gemsales-followup__input::placeholder{color:var(--gem-color-muted,#9a938a)}
.gemsales-followup__send{flex:none;width:44px;height:44px;border:0;border-radius:var(--gem-radius-md,11px);background:var(--gem-color-primary,#111);color:var(--gem-color-surface,#fff);font-size:17px;line-height:1;cursor:pointer;display:grid;place-items:center;transition:transform .15s ease}
.gemsales-followup__send:hover{transform:translateY(-1px)}
.gemsales-canvas__body>*{max-width:760px;margin-left:auto;margin-right:auto}
.gemsales-canvas__loading{color:var(--gem-color-muted,#8a8178);font-size:14px;padding:8px 0}
.gemsales-canvas__error{color:var(--gem-color-critical,#c23b3b);font-size:14px}
.gemsales-canvas .gemsales-skeleton{display:grid;gap:14px;padding:2px}
.gemsales-canvas .gemsales-typing{display:flex;align-items:center;min-height:21px;margin:0 0 4px;font:600 14px/1.45 var(--gem-font-body,system-ui);color:var(--gem-color-primary,var(--gem-color-text,#3a332b));letter-spacing:.004em}
.gemsales-canvas .gemsales-typing__caret{display:inline-block;width:2px;height:15px;margin-left:3px;background:currentColor;border-radius:1px;animation:gemsales-caret 1s steps(1) infinite}
@keyframes gemsales-caret{0%,50%{opacity:1}50.01%,100%{opacity:0}}
@media (prefers-reduced-motion: reduce){.gemsales-canvas .gemsales-typing__caret{animation:none;opacity:1}}
.gemsales-canvas .gemsales-sk{border-radius:10px;background:linear-gradient(100deg,var(--gem-color-surface-alt,#f2ede6) 30%,color-mix(in srgb,var(--gem-color-surface,#fff) 70%,var(--gem-color-surface-alt,#f2ede6)) 50%,var(--gem-color-surface-alt,#f2ede6) 70%);background-size:280% 100%;animation:gemsales-shimmer 1.5s ease-in-out infinite}
.gemsales-canvas .gemsales-sk--title{height:27px;width:76%}
.gemsales-canvas .gemsales-sk--line{height:13px}
.gemsales-canvas .gemsales-sk--short{width:52%}
.gemsales-canvas .gemsales-sk--card{height:78px;margin-top:2px}
.gemsales-canvas .gemsales-sk--row{height:60px}
.gemsales-canvas .gemsales-sk--cta{height:48px;border-radius:13px;margin-top:4px}
@keyframes gemsales-shimmer{0%{background-position:140% 0}100%{background-position:-40% 0}}
.gemsales-canvas [data-provisional]{position:relative}
.gemsales-canvas [data-provisional]::after{content:"Đang cá nhân hoá…";display:block;margin-top:12px;font-size:11px;letter-spacing:.02em;color:var(--gem-color-muted,#8a8178);text-align:center;animation:gemsales-pulse 1.4s ease-in-out infinite}
@keyframes gemsales-pulse{0%,100%{opacity:.5}50%{opacity:1}}
@media (prefers-reduced-motion: reduce){.gemsales-canvas .gemsales-sk{animation:none}.gemsales-canvas [data-provisional]::after{animation:none}.gemsales-canvas{animation:none}}
@media (max-width:720px){.gemsales-canvas{width:100%;max-height:92vh;border-radius:18px 18px 0 0}.gemsales-canvas__body{padding:20px 16px 26px}}
.gemsales-canvas .gemsales-surface{display:grid;gap:16px}
.gemsales-canvas .gemsales-block{min-width:0}
.gemsales-canvas .gemsales-block__headline{font:680 clamp(22px,5.5vw,27px)/1.08 var(--gem-font-heading,var(--gem-font-body,system-ui));letter-spacing:var(--gem-heading-letter-spacing,-.025em);text-transform:var(--gem-heading-transform,none);margin:0;text-wrap:balance}
.gemsales-canvas .gemsales-block__sub{margin:8px 0 0;font:400 15px/1.5 var(--gem-font-body,system-ui);color:var(--gem-color-muted,#6b6459)}
.gemsales-canvas .gemsales-block__title{font:650 13px/1.3 var(--gem-font-body,system-ui);letter-spacing:.02em;text-transform:uppercase;color:var(--gem-color-muted,#8a8178);margin:0 0 11px}
.gemsales-canvas .gemsales-block__body{margin:0;font:400 15px/1.62 var(--gem-font-body,system-ui);color:var(--gem-color-text,#3a332b)}
.gemsales-canvas .gemsales-block--answer .gemsales-block__body,.gemsales-canvas .gemsales-block--agitate .gemsales-block__body{color:var(--gem-color-muted,#5f584f)}
.gemsales-canvas .gemsales-block--comparison,.gemsales-canvas .gemsales-block--faq,.gemsales-canvas .gemsales-block--proof,.gemsales-canvas .gemsales-block--metric,.gemsales-canvas .gemsales-block--product{background:var(--gem-color-surface-alt,#f6f2ec);border:1px solid var(--gem-color-border,#ebe4da);border-radius:var(--gem-radius-md,16px);padding:17px 18px}
.gemsales-canvas .gemsales-block--metric{display:flex;align-items:baseline;gap:13px}
.gemsales-canvas .gemsales-metric__value{font:800 clamp(29px,8vw,36px)/.95 var(--gem-font-heading,var(--gem-font-body,system-ui));letter-spacing:-.035em;color:var(--gem-color-accent,var(--gem-color-primary,#1c1a17))}
.gemsales-canvas .gemsales-metric__detail{font:400 14px/1.45 var(--gem-font-body,system-ui);color:var(--gem-color-muted,#6b6459)}
.gemsales-canvas .gemsales-block--proof{border-left:3px solid var(--gem-color-accent,var(--gem-color-primary,#1c1a17))}
.gemsales-canvas .gemsales-block__proof-label{margin:0 0 5px;font:700 13px/1.35 var(--gem-font-body,system-ui);letter-spacing:.01em}
.gemsales-canvas .gemsales-comparison{display:grid}
.gemsales-canvas .gemsales-comparison__row{display:flex;justify-content:space-between;gap:16px;font:400 14px/1.4 var(--gem-font-body,system-ui);padding:10px 0;border-bottom:1px solid var(--gem-color-border,#e7dfd3)}
.gemsales-canvas .gemsales-comparison__row:last-child{border-bottom:0;padding-bottom:0}
.gemsales-canvas .gemsales-comparison__row:first-child{padding-top:0}
.gemsales-canvas .gemsales-comparison__label{color:var(--gem-color-muted,#6b6459)}
.gemsales-canvas .gemsales-comparison__value{font-weight:650;color:var(--gem-color-text,#2c261f);text-align:right}
.gemsales-canvas .gemsales-faq{display:grid;gap:13px}
.gemsales-canvas .gemsales-faq__q{margin:0;font:650 14px/1.35 var(--gem-font-body,system-ui)}
.gemsales-canvas .gemsales-faq__a{margin:4px 0 0;font:400 14px/1.55 var(--gem-font-body,system-ui);color:var(--gem-color-muted,#6b6459)}
.gemsales-canvas .gemsales-bundle__options{display:flex;gap:10px}
.gemsales-canvas .gemsales-bundle__option{flex:1;position:relative;border:1.5px solid var(--gem-color-border,#e6ded2);border-radius:var(--gem-radius-md,15px);padding:13px 12px;background:var(--gem-color-surface,#fff);display:grid;gap:5px;cursor:pointer;transition:border-color .15s ease,box-shadow .15s ease,transform .15s ease}
.gemsales-canvas .gemsales-bundle__option:hover{transform:translateY(-1px)}
.gemsales-canvas .gemsales-bundle__option[data-featured='true']{border-color:var(--gem-color-accent,var(--gem-color-primary,#1c1a17));box-shadow:0 12px 28px color-mix(in srgb,var(--gem-color-accent,#1c1a17) 20%,transparent)}
.gemsales-canvas .gemsales-bundle__option[data-featured='true']::after{content:"★";position:absolute;top:9px;right:11px;font-size:11px;color:var(--gem-color-accent,var(--gem-color-primary,#1c1a17))}
.gemsales-canvas .gemsales-bundle__label{font:650 13px/1.25 var(--gem-font-body,system-ui);letter-spacing:-.005em}
.gemsales-canvas .gemsales-bundle__qty{font:600 11px/1 var(--gem-font-body,system-ui);color:var(--gem-color-muted,#9a938a);text-transform:uppercase;letter-spacing:.05em}
.gemsales-canvas .gemsales-bundle__price{font:800 17px/1 var(--gem-font-heading,var(--gem-font-body,system-ui));letter-spacing:-.015em;color:var(--gem-color-text,#2c261f);margin-top:2px}
.gemsales-canvas .gemsales-bundle__save{display:inline-flex;align-items:center;gap:6px;font:700 13px var(--gem-font-body,system-ui);color:var(--gem-color-success,#2e7d4f);background:color-mix(in srgb,var(--gem-color-success,#2e7d4f) 13%,transparent);padding:6px 11px;border-radius:var(--gem-radius-pill,999px);align-self:flex-start;margin-top:10px}
.gemsales-canvas .gemsales-price{font:800 17px var(--gem-font-heading,var(--gem-font-body,system-ui));color:var(--gem-color-text,#2c261f)}
.gemsales-canvas .gemsales-badge{display:inline-flex;align-items:center;gap:7px;font:600 13px/1.3 var(--gem-font-body,system-ui);padding:9px 13px;border-radius:var(--gem-radius-sm,11px);background:var(--gem-color-surface-alt,#f2ede6);color:var(--gem-color-text,#3a332b);border:1px solid var(--gem-color-border,#ebe4da)}
.gemsales-canvas .gemsales-block--guarantee .gemsales-badge::before{content:"✓";color:var(--gem-color-success,#2e7d4f);font-weight:800}
.gemsales-canvas .gemsales-block--urgency .gemsales-badge{background:color-mix(in srgb,var(--gem-color-accent,#c0392b) 11%,transparent);color:var(--gem-color-accent,#c0392b);border-color:color-mix(in srgb,var(--gem-color-accent,#c0392b) 24%,transparent)}
.gemsales-canvas .gemsales-media{overflow:hidden;position:relative}
.gemsales-canvas .gemsales-media__img{display:block;width:100%;height:100%;object-fit:cover;transition:transform .5s cubic-bezier(.16,1,.3,1)}
.gemsales-canvas .gemsales-media[data-placeholder]{background:repeating-linear-gradient(135deg,var(--gem-color-surface-alt,#efe9e1) 0 11px,var(--gem-color-surface,#fff) 11px 22px)}
.gemsales-canvas .gemsales-hero__media{aspect-ratio:16/9;border-radius:var(--gem-radius-md,14px);margin-bottom:14px}
.gemsales-canvas .gemsales-hero__media:hover .gemsales-media__img{transform:scale(1.035)}
.gemsales-canvas .gemsales-proof__media{aspect-ratio:5/2;border-radius:var(--gem-radius-sm,10px);margin-bottom:12px}
.gemsales-canvas .gemsales-bundle__thumb{width:100%;aspect-ratio:3/2;border-radius:var(--gem-radius-sm,10px);margin-bottom:8px}
.gemsales-canvas .gemsales-block--product{display:flex;gap:14px;align-items:center}
.gemsales-canvas .gemsales-surface{gap:18px}
.gemsales-canvas .gemsales-surface>.gemsales-block:first-child .gemsales-block__headline{font-size:clamp(25px,6.2vw,33px);letter-spacing:var(--gem-heading-letter-spacing,-.032em);line-height:1.05}
.gemsales-canvas .gemsales-surface>.gemsales-block:first-child .gemsales-block__sub{font-size:16px;margin-top:11px}
.gemsales-canvas .gemsales-bundle__option[data-featured='true']{background:linear-gradient(180deg,color-mix(in srgb,var(--gem-color-accent,#c0392b) 7%,var(--gem-color-surface,#fff)) 0%,var(--gem-color-surface,#fff) 70%)}
.gemsales-canvas .gemsales-product__media{width:86px;height:86px;flex:none;border-radius:var(--gem-radius-md,12px)}
.gemsales-canvas .gemsales-product__media[data-placeholder]{background:repeating-linear-gradient(135deg,var(--gem-color-surface,#fff) 0 8px,color-mix(in srgb,var(--gem-color-border,#e6ded2) 55%,transparent) 8px 16px)}
.gemsales-canvas .gemsales-reveal{animation:gemsales-reveal .5s cubic-bezier(.16,1,.3,1) both;animation-delay:calc(var(--gem-reveal-i,0) * 55ms + .08s)}
@keyframes gemsales-reveal{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion: reduce){.gemsales-canvas .gemsales-reveal{animation:none}.gemsales-canvas .gemsales-media__img{transition:none}}
.gemsales-canvas .gemsales-product__info{display:grid;gap:5px;min-width:0}
.gemsales-canvas .gemsales-product__price{font:800 16px/1 var(--gem-font-heading,var(--gem-font-body,system-ui));letter-spacing:-.015em;color:var(--gem-color-accent,var(--gem-color-primary,#1c1a17))}
.gemsales-canvas .gemsales-reviews{display:grid;gap:10px}
.gemsales-canvas .gemsales-review{background:var(--gem-color-surface,#fff);border:1px solid var(--gem-color-border,#ebe4da);border-radius:13px;padding:13px 15px;display:grid;gap:6px}
.gemsales-canvas .gemsales-review__stars{color:var(--gem-color-warn,#d79b45);font-size:13px;letter-spacing:2px;line-height:1}
.gemsales-canvas .gemsales-review__text{margin:0;font:400 14px/1.5 var(--gem-font-body,system-ui)}
.gemsales-canvas .gemsales-review__author{font:600 12px/1.2 var(--gem-font-body,system-ui);color:var(--gem-color-muted,#8a8178)}
.gemsales-canvas .gemsales-timeline{display:grid}
.gemsales-canvas .gemsales-timeline__step{position:relative;padding:0 0 15px 20px;border-left:2px solid var(--gem-color-border,#e7dfd3)}
.gemsales-canvas .gemsales-timeline__step:last-child{padding-bottom:0}
.gemsales-canvas .gemsales-timeline__step::before{content:"";position:absolute;left:-6px;top:2px;width:10px;height:10px;border-radius:50%;background:var(--gem-color-accent,var(--gem-color-primary,#1c1a17))}
.gemsales-canvas .gemsales-timeline__label{font:650 13px/1.3 var(--gem-font-body,system-ui);display:block;margin-bottom:2px}
.gemsales-canvas .gemsales-timeline__text{margin:0;font:400 13px/1.5 var(--gem-font-body,system-ui);color:var(--gem-color-muted,#6b6459)}
.gemsales-canvas .gemsales-cta{appearance:none;border:0;cursor:pointer;width:100%;padding:15px 16px;border-radius:var(--gem-radius-md,13px);background:var(--gem-color-accent,var(--gem-color-primary,#1c1a17));color:var(--gem-color-on-accent,#fff);font:700 15px/1.1 var(--gem-font-body,system-ui);letter-spacing:-.01em;margin-top:4px;box-shadow:0 10px 24px color-mix(in srgb,var(--gem-color-accent,#1c1a17) 28%,transparent);transition:transform .12s ease}
.gemsales-canvas .gemsales-cta:hover{transform:translateY(-1px)}
@media (prefers-reduced-motion: reduce){.gemsales-canvas{scroll-behavior:auto}.gemsales-canvas .gemsales-bundle__option:hover,.gemsales-canvas .gemsales-cta:hover{transform:none}}
`;
